
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label'; // Added import for Label
import {
    Building,
    Users,
    TrendingUp,
    Euro,
    Mail,
    Phone,
    MapPin,
    Globe,
    Calendar,
    Edit,
    Crown, // Changed from UserCrown to Crown
    Briefcase
} from 'lucide-react';

// @ts-nocheck
export default function SubsidiaryDetail({ isOpen, onClose, subsidiary, departments, projects, users, onEdit }) {
    if (!subsidiary) return null;

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return "0€";
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)} M€`;
        if (amount >= 1000) return `${(amount / 1000).toFixed(0)} K€`;
        return `${amount.toLocaleString('fr-FR')} €`;
    };

    const totalBudget = projects.reduce((sum, p) => sum + (p.total_budget || 0), 0);
    const activeProjects = projects.filter(p => ['in_progress', 'approved'].includes(p.status));

    const getManagerRole = (manager) => {
        const roleLabels = {
            'ceo': 'CEO',
            'dg': 'Directeur Général',
            'deputy_dg': 'DG Adjoint',
            'cfo': 'Directeur Financier',
            'cto': 'Directeur Technique',
            'other': 'Autre'
        };
        return manager.role_label || roleLabels[manager.role] || manager.role;
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Avatar className="w-16 h-16">
                                {subsidiary.logo_url ? (
                                    <img src={subsidiary.logo_url} alt={subsidiary.name} className="w-full h-full object-cover" />
                                ) : (
                                    <AvatarFallback
                                        className="text-white font-bold text-lg"
                                        style={{backgroundColor: subsidiary.color || '#0D9488'}}
                                    >
                                        {subsidiary.code?.substring(0, 2) || subsidiary.name?.substring(0, 2)}
                                    </AvatarFallback>
                                )}
                            </Avatar>
                            <div>
                                <DialogTitle className="text-2xl">{subsidiary.name}</DialogTitle>
                                <div className="flex items-center gap-3 mt-1">
                                    <Badge variant="outline">{subsidiary.code}</Badge>
                                    <Badge className={subsidiary.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                        {subsidiary.status === 'active' ? 'Active' : 'Inactive'}
                                    </Badge>
                                    {subsidiary.industry && (
                                        <Badge variant="outline">{subsidiary.industry}</Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button onClick={() => onEdit(subsidiary)} className="gap-2">
                            <Edit className="w-4 h-4" />
                            Modifier
                        </Button>
                    </div>
                </DialogHeader>

                <div className="space-y-6">
                    {/* KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="p-2 bg-teal-100 rounded-lg">
                                    <Briefcase className="w-5 h-5 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Projets</p>
                                    <p className="text-xl font-bold">{projects.length}</p>
                                    <p className="text-xs text-green-600">{activeProjects.length} actifs</p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Euro className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Budget Total</p>
                                    <p className="text-xl font-bold">{formatCurrency(totalBudget)}</p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Équipes</p>
                                    <p className="text-xl font-bold">{users.length}</p>
                                    <p className="text-xs text-gray-500">{departments.length} départements</p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="p-2 bg-yellow-100 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-yellow-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Performance</p>
                                    <p className="text-xl font-bold">
                                        {projects.length > 0 ? Math.round((activeProjects.length / projects.length) * 100) : 0}%
                                    </p>
                                    <p className="text-xs text-gray-500">projets actifs</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Informations générales */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Building className="w-5 h-5" />
                                    Informations générales
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {subsidiary.description && (
                                    <div>
                                        <Label className="text-sm font-medium text-gray-700">Description</Label>
                                        <p className="text-sm text-gray-600 mt-1">{subsidiary.description}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    {subsidiary.business_creation_date && (
                                        <div>
                                            <Label className="text-gray-500">Date de création</Label>
                                            <p className="font-medium">{new Date(subsidiary.business_creation_date).toLocaleDateString('fr-FR')}</p>
                                        </div>
                                    )}

                                    {subsidiary.closure_date && (
                                        <div>
                                            <Label className="text-gray-500">Date de clôture</Label>
                                            <p className="font-medium">{new Date(subsidiary.closure_date).toLocaleDateString('fr-FR')}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Contact & Localisation</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {subsidiary.website && (
                                    <div className="flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-gray-500" />
                                        <a href={subsidiary.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            {subsidiary.website}
                                        </a>
                                    </div>
                                )}

                                {subsidiary.email && (
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-500" />
                                        <a href={`mailto:${subsidiary.email}`} className="text-blue-600 hover:underline">
                                            {subsidiary.email}
                                        </a>
                                    </div>
                                )}

                                {subsidiary.phone && (
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-gray-500" />
                                        <span>{subsidiary.phone}</span>
                                    </div>
                                )}

                                {(subsidiary.address || subsidiary.city) && (
                                    <div className="flex items-start gap-2">
                                        <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                                        <div>
                                            {subsidiary.address && <p>{subsidiary.address}</p>}
                                            {subsidiary.city && (
                                                <p>
                                                    {subsidiary.postal_code && `${subsidiary.postal_code} `}
                                                    {subsidiary.city}
                                                    {subsidiary.country && `, ${subsidiary.country}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Direction générale */}
                    {subsidiary.general_managers && subsidiary.general_managers.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Crown className="w-5 h-5" /> {/* Changed from UserCrown to Crown */}
                                    Direction générale
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {subsidiary.general_managers.map((manager, index) => {
                                        const user = users.find(u => u.id === manager.user_id);
                                        return (
                                            <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                                                <Avatar>
                                                    <AvatarFallback className="bg-teal-600 text-white">
                                                        {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium">{user?.full_name || 'Utilisateur inconnu'}</p>
                                                    <p className="text-sm text-gray-600">{getManagerRole(manager)}</p>
                                                    {manager.start_date && (
                                                        <p className="text-xs text-gray-500">
                                                            Depuis {new Date(manager.start_date).toLocaleDateString('fr-FR')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Départements */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Départements ({departments.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {departments.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Département</TableHead>
                                            <TableHead>Responsables</TableHead>
                                            <TableHead>Équipe</TableHead>
                                            <TableHead>Statut</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {departments.map((dept) => {
                                            const deptUsers = users.filter(u => u.department_id === dept.id);
                                            return (
                                                <TableRow key={dept.id}>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{dept.name}</p>
                                                            <p className="text-sm text-gray-500">{dept.code}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {dept.managers && dept.managers.length > 0 ? (
                                                            <div className="space-y-1">
                                                                {dept.managers.map((manager, idx) => {
                                                                    const user = users.find(u => u.id === manager.user_id);
                                                                    return (
                                                                        <div key={idx} className="text-sm">
                                                                            <span className="font-medium">{user?.full_name || 'Inconnu'}</span>
                                                                            <span className="text-gray-500 text-xs ml-1">({manager.role_label || manager.role})</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 text-sm">Aucun responsable</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium">{deptUsers.length}</span>
                                                        <span className="text-sm text-gray-500 ml-1">utilisateurs</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={dept.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                                            {dept.status === 'active' ? 'Actif' : 'Inactif'}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                    <p>Aucun département configuré</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Projets récents */}
                    {projects.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Briefcase className="w-5 h-5" />
                                    Projets récents ({projects.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {projects.slice(0, 5).map((project) => (
                                        <div key={project.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                            <div>
                                                <p className="font-medium">{project.name}</p>
                                                <p className="text-sm text-gray-500">{project.code}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold">{formatCurrency(project.total_budget)}</p>
                                                <Badge variant="outline" className="text-xs">
                                                    {project.status === 'in_progress' ? 'En cours' :
                                                        project.status === 'approved' ? 'Approuvé' :
                                                            project.status === 'completed' ? 'Terminé' : project.status}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <div className="flex justify-end pt-4">
                    <Button variant="outline" onClick={onClose}>
                        Fermer
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
